import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Receipt, Plus, Trash, Eye, X, Camera, FunnelSimple,
  CalendarBlank, CurrencyDollar, Tag
} from '@phosphor-icons/react';

export default function ReceiptGalleryPage() {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);
  const [filter, setFilter] = useState({ month: '', category: '' });
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ vendor: '', amount: '', category: 'Other', date: '', notes: '' });
  const [previewImage, setPreviewImage] = useState('');
  const fileRef = useRef(null);

  const categories = ['Groceries', 'Utilities', 'Staff Salary', 'Maintenance', 'Entertainment', 'Medical', 'Education', 'Transport', 'Shopping', 'Other'];

  const fetchReceipts = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.month) params.set('month', filter.month);
      if (filter.category) params.set('category', filter.category);
      const res = await api.get(`/receipts?${params}`);
      setReceipts(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/receipts/stats/summary');
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchReceipts(); fetchStats(); }, [api, filter]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewImage(reader.result);
      setShowUpload(true);
    };
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!previewImage) return;
    setUploading(true);
    try {
      await api.post('/receipts', {
        image: previewImage,
        vendor: form.vendor,
        amount: parseFloat(form.amount) || 0,
        category: form.category,
        date: form.date || new Date().toISOString().slice(0, 10),
        notes: form.notes
      });
      setShowUpload(false);
      setPreviewImage('');
      setForm({ vendor: '', amount: '', category: 'Other', date: '', notes: '' });
      fetchReceipts();
      fetchStats();
    } catch (e) { alert(e.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this receipt?')) return;
    try { await api.delete(`/receipts/${id}`); fetchReceipts(); fetchStats(); } catch (e) { console.error(e); }
  };

  const viewFull = async (id) => {
    try {
      const res = await api.get(`/receipts/${id}`);
      setViewReceipt(res.data);
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="receipt-gallery-page" className="space-y-8 relative">
      <div className="glow-orb-orange -top-20 -left-20 z-0" />

      {/* Header */}
      <div className="relative z-10 animate-fade-up flex items-start justify-between">
        <div>
          <p className="text-neutral-500 text-sm font-medium">{t('expenses')}</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">{t('receipt_gallery')}</h1>
        </div>
        <div className="flex items-center gap-3">
          <input type="file" ref={fileRef} accept="image/*" onChange={handleFileSelect} className="hidden" />
          <button onClick={() => fileRef.current?.click()} data-testid="upload-receipt-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#FF6B35] text-white text-xs font-semibold btn-glow hover:scale-105 transition-transform">
            <Camera size={16} /> Upload Receipt
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 relative z-10 animate-fade-up" style={{ animationDelay: '100ms' }}>
          <div className="glass-card p-4" data-testid="stat-total-receipts">
            <p className="overline mb-1">Total Receipts</p>
            <p className="text-2xl font-heading font-bold text-white">{stats.total_receipts}</p>
          </div>
          <div className="glass-card p-4" data-testid="stat-this-month">
            <p className="overline mb-1">This Month</p>
            <p className="text-2xl font-heading font-bold text-white">{stats.this_month}</p>
          </div>
          <div className="glass-card p-4" data-testid="stat-total-amount">
            <p className="overline mb-1">Total Amount</p>
            <p className="text-2xl font-heading font-bold text-white">Rs. {stats.total_amount?.toLocaleString('en-IN')}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 relative z-10 animate-fade-up" style={{ animationDelay: '150ms' }}>
        <FunnelSimple size={16} className="text-neutral-500" />
        <input type="month" value={filter.month} onChange={e => setFilter(f => ({ ...f, month: e.target.value }))}
          data-testid="filter-month"
          className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-[#FF6B35]/50" />
        <select value={filter.category} onChange={e => setFilter(f => ({ ...f, category: e.target.value }))}
          data-testid="filter-category"
          className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {(filter.month || filter.category) && (
          <button onClick={() => setFilter({ month: '', category: '' })} className="text-xs text-neutral-500 hover:text-white">Clear</button>
        )}
      </div>

      {/* Receipt Grid */}
      {receipts.length === 0 ? (
        <div className="glass-card p-12 text-center relative z-10 animate-fade-up">
          <Receipt size={48} weight="duotone" className="text-neutral-600 mx-auto mb-3" />
          <p className="text-neutral-400 text-sm">No receipts found. Upload your first receipt!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 relative z-10">
          {receipts.map((r, idx) => (
            <div key={r.receipt_id} className="glass-card overflow-hidden group animate-fade-up cursor-pointer"
              data-testid={`receipt-${r.receipt_id}`}
              style={{ animationDelay: `${200 + idx * 50}ms` }}
              onClick={() => viewFull(r.receipt_id)}>
              {/* Preview */}
              <div className="aspect-[3/4] bg-neutral-800/50 flex items-center justify-center relative overflow-hidden">
                {r.has_image ? (
                  <Receipt size={48} weight="duotone" className="text-neutral-600" />
                ) : (
                  <Receipt size={48} weight="duotone" className="text-neutral-700" />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Eye size={24} className="text-white" />
                </div>
              </div>
              {/* Info */}
              <div className="p-3">
                <p className="text-white font-medium text-sm truncate">{r.vendor || 'Unknown Vendor'}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[#FF6B35] font-semibold text-xs">Rs. {r.amount?.toLocaleString('en-IN') || '0'}</span>
                  <span className="text-neutral-500 text-[10px]">{r.date}</span>
                </div>
                <div className="mt-1">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-neutral-400">{r.category || 'Other'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
          <div className="glass-card w-full max-w-lg p-6 animate-fade-up" data-testid="upload-modal" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-semibold text-white text-lg">Upload Receipt</h3>
              <button onClick={() => setShowUpload(false)} className="text-neutral-500 hover:text-white"><X size={20} /></button>
            </div>
            {previewImage && (
              <div className="mb-4 rounded-xl overflow-hidden bg-neutral-800 max-h-48 flex items-center justify-center">
                <img src={previewImage} alt="Receipt" className="max-h-48 object-contain" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Vendor</label>
                <input type="text" value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))}
                  data-testid="upload-vendor"
                  className="w-full mt-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Amount</label>
                <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  data-testid="upload-amount"
                  className="w-full mt-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Category</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  data-testid="upload-category"
                  className="w-full mt-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Date</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  data-testid="upload-date"
                  className="w-full mt-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              </div>
            </div>
            <div className="mb-4">
              <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Notes</label>
              <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                data-testid="upload-notes"
                className="w-full mt-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            </div>
            <button onClick={handleUpload} disabled={uploading} data-testid="confirm-upload-btn"
              className="w-full py-3 rounded-xl bg-[#FF6B35] text-white font-semibold text-sm btn-glow disabled:opacity-50">
              {uploading ? 'Uploading...' : 'Upload Receipt'}
            </button>
          </div>
        </div>
      )}

      {/* View Full Receipt Modal */}
      {viewReceipt && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setViewReceipt(null)}>
          <div className="glass-card w-full max-w-2xl p-6 animate-fade-up max-h-[90vh] overflow-y-auto" data-testid="view-receipt-modal" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-semibold text-white text-lg">{viewReceipt.vendor || 'Receipt'}</h3>
              <div className="flex items-center gap-2">
                {user?.role === 'admin' && (
                  <button onClick={() => { handleDelete(viewReceipt.receipt_id); setViewReceipt(null); }}
                    data-testid="delete-receipt-btn"
                    className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"><Trash size={18} /></button>
                )}
                <button onClick={() => setViewReceipt(null)} className="text-neutral-500 hover:text-white"><X size={20} /></button>
              </div>
            </div>
            {viewReceipt.image && (
              <div className="mb-5 rounded-xl overflow-hidden bg-neutral-800">
                <img src={viewReceipt.image} alt="Receipt" className="w-full object-contain max-h-[50vh]" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CurrencyDollar size={16} className="text-[#FF6B35]" />
                <div>
                  <p className="text-neutral-500 text-[10px] uppercase">Amount</p>
                  <p className="text-white font-semibold">Rs. {viewReceipt.amount?.toLocaleString('en-IN') || '0'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Tag size={16} className="text-[#00E5FF]" />
                <div>
                  <p className="text-neutral-500 text-[10px] uppercase">Category</p>
                  <p className="text-white font-semibold">{viewReceipt.category}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CalendarBlank size={16} className="text-[#22C55E]" />
                <div>
                  <p className="text-neutral-500 text-[10px] uppercase">Date</p>
                  <p className="text-white font-semibold">{viewReceipt.date}</p>
                </div>
              </div>
              {viewReceipt.notes && (
                <div>
                  <p className="text-neutral-500 text-[10px] uppercase">Notes</p>
                  <p className="text-white">{viewReceipt.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
